import { Link } from "react-router-dom";
import { MediaReveal, Reveal } from "../motion";

/**
 * Proje kartı (Clay'in WorkItem'ı gibi): kare ya da dikey medya, 30px başlık,
 * altında tek satır açıklama.
 *
 * tile.variant:
 *   photo   -> editoryal fotoğraf, isteğe bağlı telefon çerçevesinde ekran
 *   image   -> tam kaplayan görsel (object-cover)
 *   contain -> renkli zemin üzerinde sığdırılan görsel (logo, illüstrasyon, cutout)
 *   browser -> tarayıcı çerçevesinde web ekran görüntüsü
 *   phone   -> koyu zemin üzerinde telefon çerçevesinde ekran görüntüsü
 *   pair    -> önce/sonra iki görsel
 *   logo    -> marka renginde logo
 */
const PhoneFrame = ({ src, alt, className = "" }) => (
    <div className={`rounded-[2.4rem] bg-[#1c1f27] p-[6px] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.85)] transition-transform duration-700 ease-out-expo group-hover:-translate-y-2 ${className}`}>
        <div className="relative aspect-[1206/2622] overflow-hidden rounded-[2rem] bg-black">
            <div className="absolute left-1/2 top-[10px] z-10 h-[20px] w-[78px] -translate-x-1/2 rounded-full bg-black" />
            <img src={src} alt={alt} className="h-full w-full object-cover object-top" loading="lazy" />
        </div>
    </div>
);

const BrowserFrame = ({ src, alt, position }) => (
    <div className="absolute inset-x-[8%] top-[12%] overflow-hidden rounded-t-[10px] bg-[#1c1f27] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)] transition-transform duration-700 ease-out-expo group-hover:-translate-y-2" style={{ bottom: "-2%" }}>
        <div className="flex h-[28px] items-center gap-[6px] bg-[#2a2d36] px-3">
            <span className="h-[8px] w-[8px] rounded-full bg-[#ff5f57]" />
            <span className="h-[8px] w-[8px] rounded-full bg-[#febc2e]" />
            <span className="h-[8px] w-[8px] rounded-full bg-[#28c840]" />
        </div>
        <img src={src} alt={alt} className="h-[calc(100%-28px)] w-full object-cover" style={{ objectPosition: position || "50% 0%" }} loading="lazy" />
    </div>
);

const TileMedia = ({ tile, title }) => {
    const aspect = tile.aspect === "portrait" ? "aspect-[4/5]" : "aspect-square";
    const hover = "transition-transform duration-[1200ms] ease-out-expo group-hover:scale-[1.04]";

    switch (tile.variant) {
        case "photo": {
            const side = tile.screenSide === "right" ? "right-[8%]" : "left-1/2 -translate-x-1/2";
            return (
                <div className={`${aspect} relative overflow-hidden bg-ink-900`}>
                    <img src={tile.src} alt={title} className={`h-full w-full object-cover ${hover}`} style={{ objectPosition: tile.position || "50% 50%" }} loading="lazy" />
                    {tile.screen && (
                        <>
                            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(6,7,10,0)_40%,rgba(6,7,10,0.55)_100%)]" />
                            <PhoneFrame src={tile.screen} alt={`${title} uygulama ekranı`} className={`absolute bottom-[-14%] ${side} w-[44%] max-w-[260px]`} />
                        </>
                    )}
                </div>
            );
        }
        case "contain":
            return (
                <div className={`${aspect} relative flex items-center justify-center overflow-hidden`} style={{ backgroundColor: tile.bg, padding: tile.pad || "10%" }}>
                    {tile.backdrop && (
                        <img src={tile.backdrop} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-80" loading="lazy" />
                    )}
                    <img src={tile.src} alt={title} className={`relative max-h-full max-w-full object-contain ${hover}`} loading="lazy" />
                </div>
            );
        case "browser":
            return (
                <div className={`${aspect} relative overflow-hidden`} style={{ backgroundColor: tile.bg }}>
                    <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_20%,rgba(255,255,255,0.10),transparent)]" />
                    <BrowserFrame src={tile.src} alt={`${title} ekran görüntüsü`} position={tile.position} />
                </div>
            );
        case "phone":
            return (
                <div className={`${aspect} relative flex items-end justify-center overflow-hidden pt-[12%]`} style={{ backgroundColor: tile.bg }}>
                    <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_30%,rgba(255,255,255,0.10),transparent)]" />
                    <div className="relative translate-y-[18%]">
                        <PhoneFrame src={tile.src} alt={`${title} uygulama ekranı`} className="w-[62%] max-w-[300px]" />
                    </div>
                </div>
            );
        case "pair":
            return (
                <div className={`${aspect} grid grid-cols-2 gap-[6px] overflow-hidden`} style={{ backgroundColor: tile.bg }}>
                    {[["Önce", tile.before], ["Sonra", tile.after]].map(([label, src]) => (
                        <div key={label} className="relative overflow-hidden">
                            <img src={src} alt={`${title} ${label.toLowerCase()}`} className={`h-full w-full object-cover ${hover}`} loading="lazy" />
                            <span className="absolute bottom-3 left-3 rounded-full bg-ink/70 px-3 py-1 text-[12px] font-medium text-white">{label}</span>
                        </div>
                    ))}
                </div>
            );
        case "logo":
            return (
                <div className={`${aspect} flex items-center justify-center`} style={{ backgroundColor: tile.bg }}>
                    <img src={tile.src} alt={`${title} logosu`} className={`w-[38%] max-w-[240px] rounded-[22%] ${hover}`} loading="lazy" />
                </div>
            );
        default:
            return (
                <div className={`${aspect} overflow-hidden`} style={{ backgroundColor: tile.bg }}>
                    <img src={tile.src} alt={title} className={`h-full w-full object-cover ${hover}`} style={{ objectPosition: tile.position || "50% 50%" }} loading="lazy" />
                </div>
            );
    }
};

const WorkItem = ({ item, index = 0 }) => {
    const delay = (index % 2) * 0.1;
    return (
        <article>
            <Link to={`/projeler/${item.slug}`} className="group block">
                <MediaReveal delay={delay}>
                    <TileMedia tile={item.tile} title={item.title} />
                </MediaReveal>
                <Reveal delay={delay + 0.1} className="mt-6">
                    <p className="t-small text-ink-500">{item.client}{item.sector ? ` · ${item.sector}` : ""}</p>
                    <h3 className="t-h3 mt-1 transition-colors duration-300 group-hover:text-ink-600">{item.title}</h3>
                    <p className="t-body mt-1 text-ink-600">{item.blurb}</p>
                </Reveal>
            </Link>
        </article>
    );
};

export default WorkItem;
